const React = require('react');
const { createContext, useContext, useReducer, useEffect } = React;
const AsyncStorage = require('@react-native-async-storage/async-storage').default;

// Business State Context
const BusinessStateContext = createContext();

// Initial state
const initialState = {
  // Business data
  jobs: [],
  campaigns: [],
  projects: [],
  candidates: [],
  interviews: [],
  offers: [],
  
  // UI state
  loading: {
    jobs: false,
    campaigns: false,
    projects: false,
    candidates: false,
    interviews: false,
    offers: false,
  },
  
  error: {
    jobs: null,
    campaigns: null,
    projects: null,
    candidates: null,
    interviews: null,
    offers: null,
  },
  
  // Statistics
  stats: {
    totalJobs: 0,
    activeCampaigns: 0,
    ongoingProjects: 0,
    totalCandidates: 0,
    scheduledInterviews: 0,
    pendingOffers: 0,
  },
  
  // User preferences
  preferences: {
    notifications: true,
    autoSync: true,
    theme: 'light',
  },
  
  // Cache
  lastUpdated: null,
  isInitialized: false,
};

// Action types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_JOBS: 'SET_JOBS',
  ADD_JOB: 'ADD_JOB',
  UPDATE_JOB: 'UPDATE_JOB',
  DELETE_JOB: 'DELETE_JOB',
  SET_CAMPAIGNS: 'SET_CAMPAIGNS',
  ADD_CAMPAIGN: 'ADD_CAMPAIGN',
  UPDATE_CAMPAIGN: 'UPDATE_CAMPAIGN',
  DELETE_CAMPAIGN: 'DELETE_CAMPAIGN',
  SET_PROJECTS: 'SET_PROJECTS',
  ADD_PROJECT: 'ADD_PROJECT',
  UPDATE_PROJECT: 'UPDATE_PROJECT',
  DELETE_PROJECT: 'DELETE_PROJECT',
  SET_CANDIDATES: 'SET_CANDIDATES',
  ADD_CANDIDATE: 'ADD_CANDIDATE',
  UPDATE_CANDIDATE: 'UPDATE_CANDIDATE',
  DELETE_CANDIDATE: 'DELETE_CANDIDATE',
  SET_INTERVIEWS: 'SET_INTERVIEWS',
  ADD_INTERVIEW: 'ADD_INTERVIEW',
  UPDATE_INTERVIEW: 'UPDATE_INTERVIEW',
  DELETE_INTERVIEW: 'DELETE_INTERVIEW',
  SET_OFFERS: 'SET_OFFERS',
  ADD_OFFER: 'ADD_OFFER',
  UPDATE_OFFER: 'UPDATE_OFFER',
  DELETE_OFFER: 'DELETE_OFFER',
  UPDATE_STATS: 'UPDATE_STATS',
  UPDATE_PREFERENCES: 'UPDATE_PREFERENCES',
  SET_INITIALIZED: 'SET_INITIALIZED',
  RESET_STATE: 'RESET_STATE',
};

// Reducer
const businessReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        loading: {
          ...state.loading,
          [action.payload.key]: action.payload.value,
        },
      };
      
    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: {
          ...state.error,
          [action.payload.key]: action.payload.value,
        },
      };
      
    case ActionTypes.SET_JOBS:
      return {
        ...state,
        jobs: action.payload,
        lastUpdated: new Date().toISOString(),
      };
      
    case ActionTypes.ADD_JOB:
      return {
        ...state,
        jobs: [...state.jobs, action.payload],
        lastUpdated: new Date().toISOString(),
      };
      
    case ActionTypes.UPDATE_JOB:
      return {
        ...state,
        jobs: state.jobs.map(job => 
          job.id === action.payload.id ? { ...job, ...action.payload } : job
        ),
        lastUpdated: new Date().toISOString(),
      };
      
    case ActionTypes.DELETE_JOB:
      return {
        ...state,
        jobs: state.jobs.filter(job => job.id !== action.payload),
        lastUpdated: new Date().toISOString(),
      };
      
    case ActionTypes.SET_CAMPAIGNS:
      return {
        ...state,
        campaigns: action.payload,
        lastUpdated: new Date().toISOString(),
      };
      
    case ActionTypes.ADD_CAMPAIGN:
      return {
        ...state,
        campaigns: [...state.campaigns, action.payload],
        lastUpdated: new Date().toISOString(),
      };
      
    case ActionTypes.UPDATE_CAMPAIGN:
      return {
        ...state,
        campaigns: state.campaigns.map(campaign => 
          campaign.id === action.payload.id ? { ...campaign, ...action.payload } : campaign
        ),
        lastUpdated: new Date().toISOString(),
      };
      
    case ActionTypes.DELETE_CAMPAIGN:
      return {
        ...state,
        campaigns: state.campaigns.filter(campaign => campaign.id !== action.payload),
        lastUpdated: new Date().toISOString(),
      };
      
    case ActionTypes.UPDATE_STATS:
      return {
        ...state,
        stats: {
          ...state.stats,
          ...action.payload,
        },
      };
      
    case ActionTypes.UPDATE_PREFERENCES:
      return {
        ...state,
        preferences: {
          ...state.preferences,
          ...action.payload,
        },
      };
      
    case ActionTypes.SET_INITIALIZED:
      return {
        ...state,
        isInitialized: action.payload,
      };
      
    case ActionTypes.RESET_STATE:
      return {
        ...initialState,
        preferences: state.preferences,
      };
      
    default:
      return state;
  }
};

// Business State Provider
const BusinessStateProvider = ({ children }) => {
  const [state, dispatch] = useReducer(businessReducer, initialState);

  // Initialize state from storage
  useEffect(() => {
    initializeState();
  }, []);

  // Save state to storage when it changes
  useEffect(() => {
    if (state.isInitialized) {
      saveStateToStorage();
    }
  }, [state]);

  const initializeState = async () => {
    try {
      console.log('🔄 Initializing BusinessStateManager...');
      
      const savedState = await AsyncStorage.getItem('businessState');
      if (savedState) {
        const parsedState = JSON.parse(savedState);
        
        if (parsedState.jobs) {
          dispatch({ type: ActionTypes.SET_JOBS, payload: parsedState.jobs });
        }
        if (parsedState.campaigns) {
          dispatch({ type: ActionTypes.SET_CAMPAIGNS, payload: parsedState.campaigns });
        }
        if (parsedState.preferences) {
          dispatch({ type: ActionTypes.UPDATE_PREFERENCES, payload: parsedState.preferences });
        }
        
        console.log('✅ BusinessState restored from storage');
      }
      
      updateStatistics();
      dispatch({ type: ActionTypes.SET_INITIALIZED, payload: true });
      console.log('✅ BusinessStateManager initialized successfully');
      
    } catch (error) {
      console.error('❌ Error initializing BusinessStateManager:', error);
      dispatch({ type: ActionTypes.SET_INITIALIZED, payload: true });
    }
  };

  const saveStateToStorage = async () => {
    try {
      const stateToSave = {
        jobs: state.jobs,
        campaigns: state.campaigns,
        projects: state.projects,
        candidates: state.candidates,
        interviews: state.interviews,
        offers: state.offers,
        preferences: state.preferences,
        lastUpdated: state.lastUpdated,
      };
      
      await AsyncStorage.setItem('businessState', JSON.stringify(stateToSave));
      console.log('💾 BusinessState saved to storage');
    } catch (error) {
      console.error('❌ Error saving BusinessState:', error);
    }
  };

  const updateStatistics = () => {
    const stats = {
      totalJobs: state.jobs.length,
      activeCampaigns: state.campaigns.filter(c => c.status === 'active').length,
      ongoingProjects: state.projects.filter(p => p.status === 'ongoing').length,
      totalCandidates: state.candidates.length,
      scheduledInterviews: state.interviews.filter(i => i.status === 'scheduled').length,
      pendingOffers: state.offers.filter(o => o.status === 'pending').length,
    };
    
    dispatch({ type: ActionTypes.UPDATE_STATS, payload: stats });
  };

  // Action creators
  const actions = {
    setLoading: (key, value) => {
      dispatch({ type: ActionTypes.SET_LOADING, payload: { key, value } });
    },
    
    setError: (key, value) => {
      dispatch({ type: ActionTypes.SET_ERROR, payload: { key, value } });
    },
    
    addJob: (job) => {
      const newJob = {
        ...job,
        id: job.id || `job_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      dispatch({ type: ActionTypes.ADD_JOB, payload: newJob });
      updateStatistics();
    },
    
    updateJob: (jobId, updates) => {
      const updatedJob = {
        ...updates,
        id: jobId,
        updatedAt: new Date().toISOString(),
      };
      dispatch({ type: ActionTypes.UPDATE_JOB, payload: updatedJob });
      updateStatistics();
    },
    
    deleteJob: (jobId) => {
      dispatch({ type: ActionTypes.DELETE_JOB, payload: jobId });
      updateStatistics();
    },
    
    addCampaign: (campaign) => {
      const newCampaign = {
        ...campaign,
        id: campaign.id || `campaign_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      dispatch({ type: ActionTypes.ADD_CAMPAIGN, payload: newCampaign });
      updateStatistics();
    },
    
    updatePreferences: (preferences) => {
      dispatch({ type: ActionTypes.UPDATE_PREFERENCES, payload: preferences });
    },
    
    resetState: () => {
      dispatch({ type: ActionTypes.RESET_STATE });
      AsyncStorage.removeItem('businessState');
    },
  };

  const value = {
    state,
    actions,
    isInitialized: state.isInitialized,
  };

  return React.createElement(
    BusinessStateContext.Provider,
    { value },
    children
  );
};

// Custom hooks
const useBusinessState = () => {
  const context = useContext(BusinessStateContext);
  if (!context) {
    throw new Error('useBusinessState must be used within a BusinessStateProvider');
  }
  return context;
};

const useJobs = () => {
  const { state } = useBusinessState();
  return {
    jobs: state.jobs,
    loading: state.loading.jobs,
    error: state.error.jobs,
  };
};

const useCampaigns = () => {
  const { state } = useBusinessState();
  return {
    campaigns: state.campaigns,
    loading: state.loading.campaigns,
    error: state.error.campaigns,
  };
};

const useBusinessStats = () => {
  const { state } = useBusinessState();
  return state.stats;
};

module.exports = {
  BusinessStateProvider,
  useBusinessState,
  useJobs,
  useCampaigns,
  useBusinessStats,
};

